import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import { logoAltUrl } from '../data/mock';
import { blogPosts } from '../data/blogPosts';
import { useSEO } from '../hooks/useSEO';

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });

const Blog = () => {
  useSEO(
    'Electrical Tips & Insights Blog | Nexline Electrical Ltd.',
    "Read expert electrical tips from Nexline Electrical Ltd. — panel upgrades, EV charger installation, and commercial maintenance advice for Canadians."
  );

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 h-24 flex items-center justify-between">
          <Link to="/" data-testid="blog-logo-home-link" className="flex items-center gap-3">
            <img src={logoAltUrl} alt="Nexline Electrical" className="h-14 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="blog-back-home-link"
            className="flex items-center gap-2 text-neutral-400 hover:text-electric-yellow text-sm font-medium transition-colors duration-300"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-16 lg:py-24" data-testid="blog-listing-page">
        <p className="text-electric-yellow text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2">
          <Zap size={14} />
          Insights
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Electrical Tips & Insights</h1>
        <p className="text-neutral-400 max-w-2xl mb-14">
          Practical advice from our licensed electricians on panel upgrades, EV charging, and commercial
          electrical maintenance across Canada.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group block bg-neutral-950 border border-white/5 hover:border-electric-yellow/40 transition-all duration-300"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <span className="text-electric-yellow text-xs font-semibold uppercase tracking-wider">
                  {post.category}
                </span>
                <h2 className="text-xl font-semibold text-white mt-3 mb-3 leading-snug group-hover:text-electric-yellow transition-colors duration-300">
                  {post.title}
                </h2>
                <p className="text-neutral-400 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                <div className="flex items-center justify-between text-sm text-neutral-500">
                  <span>{formatDate(post.date)}</span>
                  <span className="inline-flex items-center gap-1 text-electric-yellow opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Read <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Blog;
